import { useState, useEffect, useRef } from "react";
import "./ResiliationDialog.css";

type DataChoice = "keep" | "delete" | null;
type Step = "warning" | "data-choice" | "confirm" | "done";

interface ResiliationDialogProps {
  isOpen: boolean;
  companyName?: string;
  isPending?: boolean;
  onCancel: () => void;
  onConfirm: (deleteData: boolean) => void;
  t: {
    cancel: string;
    confirm: string;
    continue: string;
    back: string;

    warningTitle: string;
    warningDesc1: string;
    warningDescIrreversible: string;
    warningDesc2: string;
    warningDescImmediate: string;
    warningDesc3: string;
    warningDescClosure: string;
    warningDesc4: string;
    warningPoint1: string;
    warningPoint2: string;
    warningPoint3: string;
    warningPoint4: string;

    step1Label: string;
    dataChoiceTitle: string;
    dataChoiceDesc: string;
    keepDataTitle: string;
    keepDataDesc1: string;
    deleteDataTitle: string;
    deleteDataDesc1: string;
    deleteDataDescStrong: string;
    deleteDataDesc2: string;

    step2Label: string;
    confirmDeleteTitle: string;
    confirmKeepTitle: string;
    confirmDeleteDesc1: string;
    confirmDeleteDescStrong: string;
    confirmDeleteDesc2: string;
    confirmDeleteDesc3: string;
    confirmKeepDesc1: string;
    confirmKeepDescStrong: string;
    confirmKeepDesc2: string;

    summaryResiliation: string;
    summaryImmediate: string;
    summaryData: string;
    summaryDataDeleted: string;
    summaryDataKept: string;

    confirmingBtn: string;
    confirmLeaveBtn: string;

    doneTitle: string;
    doneMessageDeleted: string;
    doneMessageKept: string;
    redirecting: string;
  };
}

const IconWarning = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const IconArchive = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <polyline points="21 8 21 21 3 21 3 8" />
    <rect x="1" y="3" width="22" height="5" />
    <line x1="10" y1="12" x2="14" y2="12" />
  </svg>
);

const IconTrash = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

const IconCheck = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const IconClose = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export default function ResiliationDialog({
  isOpen,
  companyName = "SUPPRIMER",
  isPending = false,
  onCancel,
  onConfirm,
  t,
}: ResiliationDialogProps) {
  const [step, setStep] = useState<Step>("warning");
  const [dataChoice, setDataChoice] = useState<DataChoice>(null);
  const [confirmInput, setConfirmInput] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setStep("warning");
      setDataChoice(null);
      setConfirmInput("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (step === "confirm" && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [step]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  const confirmWord =
    dataChoice === "delete" ? companyName.toUpperCase() || "SUPPRIMER" : null;

  const canConfirm =
    dataChoice === "delete"
      ? confirmInput.trim().toUpperCase() === confirmWord
      : dataChoice === "keep";

  const handleConfirm = () => {
    if (!canConfirm || isPending) return;
    if (step === "confirm") {
      onConfirm(dataChoice === "delete");
      setStep("done");
    }
  };

  const renderStepWarning = () => (
    <>
      <div className="resil-icon resil-icon--warning">
        <IconWarning />
      </div>

      <h2 className="resil-title">{t.warningTitle}</h2>

      <p className="resil-desc">
        {t.warningDesc1} <strong>{t.warningDescIrreversible}</strong>
        {t.warningDesc2} <strong>{t.warningDescImmediate}</strong>{" "}
        {t.warningDesc3} <strong>{t.warningDescClosure}</strong>{" "}
        {t.warningDesc4}
      </p>

      <ul className="resil-consequences">
        <li>
          <span className="resil-consequences-dot" />
          {t.warningPoint1}
        </li>
        <li>
          <span className="resil-consequences-dot" />
          {t.warningPoint2}
        </li>
        <li>
          <span className="resil-consequences-dot" />
          {t.warningPoint3}
        </li>
        <li>
          <span className="resil-consequences-dot" />
          {t.warningPoint4}
        </li>
      </ul>

      <div className="resil-actions">
        <button
          type="button"
          className="resil-btn resil-btn--ghost"
          onClick={onCancel}
        >
          {t.cancel}
        </button>
        <button
          type="button"
          className="resil-btn resil-btn--danger-outline"
          onClick={() => setStep("data-choice")}
        >
          {t.continue} →
        </button>
      </div>
    </>
  );

  const renderStepDataChoice = () => (
    <>
      <div className="resil-step-label">{t.step1Label}</div>
      <h2 className="resil-title">{t.dataChoiceTitle}</h2>
      <p className="resil-desc">{t.dataChoiceDesc}</p>

      <div className="resil-choices">
        <button
          type="button"
          className={`resil-choice-card ${dataChoice === "keep" ? "selected" : ""}`}
          onClick={() => setDataChoice("keep")}
        >
          <div
            className={`resil-choice-icon resil-choice-icon--keep ${dataChoice === "keep" ? "selected" : ""}`}
          >
            <IconArchive />
          </div>
          <div className="resil-choice-body">
            <span className="resil-choice-title">{t.keepDataTitle}</span>
            <span className="resil-choice-sub">
              {t.keepDataDesc1} <em>balasubramaniam.rishi@gmail.com</em>.
            </span>
          </div>
          <div
            className={`resil-choice-radio ${dataChoice === "keep" ? "selected" : ""}`}
          />
        </button>

        <button
          type="button"
          className={`resil-choice-card resil-choice-card--danger ${dataChoice === "delete" ? "selected" : ""}`}
          onClick={() => setDataChoice("delete")}
        >
          <div
            className={`resil-choice-icon resil-choice-icon--delete ${dataChoice === "delete" ? "selected" : ""}`}
          >
            <IconTrash />
          </div>
          <div className="resil-choice-body">
            <span className="resil-choice-title">{t.deleteDataTitle}</span>
            <span className="resil-choice-sub">
              {t.deleteDataDesc1} <strong>{t.deleteDataDescStrong}</strong>
              {t.deleteDataDesc2}
            </span>
          </div>
          <div
            className={`resil-choice-radio resil-choice-radio--danger ${dataChoice === "delete" ? "selected" : ""}`}
          />
        </button>
      </div>

      <div className="resil-actions">
        <button
          type="button"
          className="resil-btn resil-btn--ghost"
          onClick={() => setStep("warning")}
        >
          ← {t.back}
        </button>
        <button
          type="button"
          className="resil-btn resil-btn--danger"
          disabled={dataChoice === null}
          onClick={() => setStep("confirm")}
        >
          {t.continue} →
        </button>
      </div>
    </>
  );

  const renderStepConfirm = () => (
    <>
      <div className="resil-step-label">{t.step2Label}</div>
      <h2 className="resil-title">
        {dataChoice === "delete" ? t.confirmDeleteTitle : t.confirmKeepTitle}
      </h2>

      {dataChoice === "delete" ? (
        <>
          <p className="resil-desc">
            {t.confirmDeleteDesc1} <strong>{t.confirmDeleteDescStrong}</strong>{" "}
            {t.confirmDeleteDesc2}{" "}
            <code className="resil-code">{confirmWord}</code>{" "}
            {t.confirmDeleteDesc3}
          </p>
          <div className="resil-input-wrapper">
            <input
              ref={inputRef}
              type="text"
              className="resil-confirm-input"
              placeholder={confirmWord ?? ""}
              value={confirmInput}
              onChange={(e) => setConfirmInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleConfirm()}
              autoComplete="off"
              spellCheck={false}
            />
          </div>
        </>
      ) : (
        <p className="resil-desc">
          {t.confirmKeepDesc1} <strong>{t.confirmKeepDescStrong}</strong>
          {t.confirmKeepDesc2}
        </p>
      )}

      <div className="resil-summary">
        <div className="resil-summary-row">
          <span>{t.summaryResiliation}</span>
          <span className="resil-summary-value resil-summary-value--red">
            {t.summaryImmediate}
          </span>
        </div>
        <div className="resil-summary-row">
          <span>{t.summaryData}</span>
          <span
            className={`resil-summary-value ${dataChoice === "delete" ? "resil-summary-value--red" : "resil-summary-value--blue"}`}
          >
            {dataChoice === "delete" ? t.summaryDataDeleted : t.summaryDataKept}
          </span>
        </div>
      </div>

      <div className="resil-actions">
        <button
          type="button"
          className="resil-btn resil-btn--ghost"
          onClick={() => setStep("data-choice")}
        >
          ← {t.back}
        </button>
        <button
          type="button"
          className="resil-btn resil-btn--danger"
          disabled={!canConfirm || isPending}
          onClick={handleConfirm}
        >
          {isPending ? t.confirmingBtn : t.confirmLeaveBtn}
        </button>
      </div>
    </>
  );

  const renderStepDone = () => (
    <div className="resil-done">
      <div className="resil-done-icon">
        <IconCheck />
      </div>
      <h2 className="resil-title">{t.doneTitle}</h2>
      <p className="resil-desc">
        {dataChoice === "delete" ? t.doneMessageDeleted : t.doneMessageKept}
      </p>
      <p className="resil-desc resil-desc--muted">{t.redirecting}</p>
    </div>
  );

  return (
    <div className="resil-overlay" role="dialog" aria-modal="true">
      <div className="resil-panel">
        {step !== "done" && (
          <button
            type="button"
            className="resil-close"
            onClick={onCancel}
            aria-label={t.cancel}
          >
            <IconClose />
          </button>
        )}

        {step !== "done" && (
          <div className="resil-progress">
            <div
              className="resil-progress-bar"
              style={{
                width:
                  step === "warning"
                    ? "33.3%"
                    : step === "data-choice"
                      ? "66.6%"
                      : "100%",
              }}
            />
          </div>
        )}

        <div className="resil-body">
          {step === "warning" && renderStepWarning()}
          {step === "data-choice" && renderStepDataChoice()}
          {step === "confirm" && renderStepConfirm()}
          {step === "done" && renderStepDone()}
        </div>
      </div>
    </div>
  );
}
